'use client';

// Автоформа настроек шага (этап 5 строителя — docs/archive/SCENARIO-BUILDER-PLAN.md, п.3).
// Поля генерятся из node_catalog.paramsJson (INodeProperties[]): string/number/boolean/
// options/multiOptions + displayOptions (видимость считает visibleParams — ТОТ ЖЕ
// вычислитель, что у линта). Сложные типы (collection/fixedCollection/…) — v1 честно
// редактируются как JSON-текст с подсказкой; выражения {{ … }} — обычный текст с хинтом.
// INVARIANT(scriptera): «что рисовать полю» решает ТОЛЬКО dispatchField (field-dispatch.ts)
// — рендерер не добавляет своих веток по type/typeOptions. Причина: по той же таблице
// контролёр form-audit.mjs считает дыры покрытия; своя ветка здесь = враньё аудита.
// Канон: docs/concepts/FORM-ENGINE-PLAN.md (§1, §6).
import { createContext, useContext, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import styles from './scenario.module.css';
import { effectiveNodeParams, visibleCollectionSubs, visibleParams, type CatalogParam, type CatalogParamOption } from './graph';
import {
  collectionSubParams,
  dispatchField,
  dynFieldQuery,
  fixedGroups,
  inputPassport,
  isPlainObject,
  rlModes,
  rmConfig,
  scalarOptions,
  SIMPLE_TYPES,
  typeOpt,
  type DynOption,
  type DynOptionsLoader,
  type FcGroup,
  type WidgetName,
} from './field-dispatch';
import { datePickByInner, humanDateExpr, livePickValue, type DateCompare } from './date-picks';
import { ISO_DATE_SAMPLE_RE, type ShapeField } from './data-shape';
import { humanPath, parseExpression } from './expression';
import { ASSIGN_TYPES, FILTER_OPS, findOp, opKey } from './filter-ops';
import { SmartSelect } from './smart-select';
import { KChoice, KChoiceMany } from '../kit/form';
import { wantsPickSheet } from '../kit/select';
import { KSwitch } from '../kit/toggle';
import { KBareButton } from '../kit/button';
import { binKindWord, DataPickPanel, type DataPick } from './data-picker';
import { evalFormulaPreview } from './formula-eval';
import { lintFormula, type FormulaLintIssue } from './formula-lint';
import { applySuggestion, filterSuggestions, formulaCandidates, formulaDotSuggestions, formulaTokenAt } from './formula-suggest';
import type { FormulaSuggestion } from './formula-suggest';

export interface ParamsFormDict {
  required: string; // пометка обязательного поля
  noParams: string; // «у шага нет настроек»
  complexHint: string; // подсказка у JSON-поля сложного типа
  jsonInvalid: string; // «не похоже на JSON»
  exprHint: string; // подсказка про {{ … }} внизу формы
  advanced: string; // заголовок свёрнутой секции «Продвинутое»
  /** Ф19: заголовок свёртки незаполненных необязательных («Ещё N настроек»). */
  moreSettings: (n: number) => string;
  lang: 'ru' | 'en'; // язык словаря путей (плашка подстановок)
  substAuto: string; // «Подставится само:»
  substFromStep: string; // «из шага «{step}»» — {step} заменяется кодом
  substPrev: string; // «из данных предыдущего шага»
  substFormula: string; // «формула по данным шагов» (незнакомая форма выражения)
  substEdit: string; // кнопка «изменить» на плашке
  collAdd: string; // «+ Добавить параметр» (collection)
  collAddItem: string; // «+ Добавить» (повторяемая группа fixedCollection)
  collRemove: string; // aria/подпись «убрать»
  condAll: string; // «выполняются все условия (И)»
  condAny: string; // «любое из условий (ИЛИ)»
  condAdd: string; // «+ Ещё условие»
  condLeft: string; // подпись «Что проверяем»
  condOp: string; // подпись «Условие»
  condRight: string; // подпись «С чем сравнить»
  assignAdd: string; // «+ Ещё поле»
  assignName: string; // подпись «Поле»
  assignType: string; // подпись «Тип»
  assignValue: string; // подпись «Значение»
  pickBtn: string; // кнопка у строкового поля «Взять из данных» (title/aria)
  pickLoading: string; // «Смотрю данные шагов…»
  pickEmpty: string; // данных нет ни из прогона, ни из образцов
  pickTestRun: string; // кнопка «Сделать пробный запуск — подхвачу структуру»
  pickRunning: string; // «Запускаю…»
  pickSampleTag: string; // бейдж «примерная структура» у шага-образца
  pickFromSteps: string; // подпись раздела пикера «Из шагов»
  pickStandard: string; // подпись раздела пикера «Стандартные»
  pickDates: string; // группа «Дата и время» в «Стандартных»
  pickCounters: string; // группа «Счётчики и запуск» в «Стандартных»
  pickCustom: string; // пункт «Своя формула» в пикере
  pickNoMatch: string; // §3.2: в шагах нет полей подходящего типа (фильтр всё отсёк)
  pickNoFile: string; // §3б: пункт «Без файла» у бинарного входа
  binImage: string; // §3б/§3.3: слова вида файла для пикера и плашки
  binVideo: string;
  binAudio: string;
  binFile: string;
  dayMonthHint: string; // подсказка формата «число.месяц, например 08.07»
  previewNow: string; // «Сейчас получится:» — предпросмотр формулы
  rlInvalid: string; // resourceLocator: значение не похоже на формат режима (regex)
  rlSearch: string; // resourceLocator list: placeholder поля поиска («Поиск…»)
  rlSearchType: string; // list c searchFilterRequired: «начни печатать — поищу в списке»
  rlEmpty: string; // list без вариантов: «список пуст или не загрузился — можно вписать руками»
  rmDefine: string; // resourceMapper: чип режима «указать поля здесь»
  rmAuto: string; // resourceMapper: чип режима «автоматически из данных»
  rmAutoHint: string; // resourceMapper: пояснение авторежима (по совпадению имён)
  rmMatch: string; // resourceMapper update/upsert: подпись «искать запись по»
  rmAdd: string; // resourceMapper: «+ Добавить поле» (колонку)
  rmNeedSchema: string; // resourceMapper без схемы: «выбери доступ и источник — подхвачу поля»
  fxBraces: string; // формула не закрыта — нет пары {{ }}
  fxBracket: string; // в формуле не сходятся скобки ( ) [ ]
  fxQuote: string; // в формуле не закрыта кавычка
  fxEmpty: string; // формула пустая — внутри {{ }} ничего нет
  fxNoStep: string; // «шага «{step}» нет выше» — {step} заменяется кодом
}

// Код ошибки проверяльщика формул (formula-lint) → человеческая подпись словаря.
function lintText(issue: FormulaLintIssue, dict: ParamsFormDict): string {
  if (issue.code === 'braces') return dict.fxBraces;
  if (issue.code === 'bracket') return dict.fxBracket;
  if (issue.code === 'quote') return dict.fxQuote;
  if (issue.code === 'empty') return dict.fxEmpty;
  return dict.fxNoStep.replace('{step}', issue.step ?? '');
}

const humanize = (key: string): string =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();

const label = (p: CatalogParam): string => p.displayName?.trim() || humanize(p.name);

// Описания/notice в paramsJson n8n бывают с HTML (<a>, <br>, <code>) — в нашу форму
// идёт только текст: теги вон, <br> — перенос, базовые entity — символами.
function stripHtml(s: string): string {
  return s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Пояснение поля: наш текст-объяснение (этап 9, node_help) в приоритете; иначе —
// исходные hint/description ноды. Наш текст — уже человеческий, HTML-очистка не нужна.
function fieldDesc(p: CatalogParam, help?: string): string {
  if (help && help.trim()) return help.trim();
  return stripHtml(p.hint || p.description || '');
}

/**
 * Поле текста РАСТЁТ ПО НАБОРУ, а не держит заранее отведённые строки (владелец
 * 2026-08-06: «ты ему подготовил три строки, и даже если занята одна — большой отступ;
 * пусть растёт и сдвигает контент вниз»). Высоту меряем по фактическому содержимому
 * (scrollHeight), а не по числу переводов строки: на телефоне длинная фраза переносится
 * сама, и счёт «\n» её не увидит. Потолок — чтобы простыня не съела экран целиком.
 */
export function AutoGrowTextarea({
  className,
  value,
  placeholder,
  max,
  rows,
  ariaLabel,
  maxLength,
  onValue,
  onBlur,
}: {
  className?: string;
  value: string;
  placeholder?: string;
  /** Потолок в строках — дальше поле начинает скроллиться внутри себя. */
  max: number;
  /** Строк по анкете — только для CSS (`--ta-rows`), высоту поля в вебе ведёт автророст. */
  rows?: number;
  ariaLabel?: string;
  maxLength?: number;
  onValue: (v: string) => void;
  onBlur?: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Сброс перед замером обязателен: иначе scrollHeight запомнит прошлую, большую высоту
    // и поле никогда не уменьшится.
    el.style.height = 'auto';
    const line = Number.parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.style.height = `${Math.min(el.scrollHeight, Math.round(line * max))}px`;
  }, [value, max]);
  return (
    <textarea
      ref={ref}
      className={className}
      rows={1}
      {...(rows ? { style: { '--ta-rows': rows } as CSSProperties } : {})}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      maxLength={maxLength}
      onChange={(e) => onValue(e.target.value)}
      onBlur={onBlur}
    />
  );
}

function FieldShell({
  p,
  dict,
  help,
  children,
  onFill,
}: {
  p: CatalogParam;
  dict: ParamsFormDict;
  help?: string;
  children: React.ReactNode;
  /** QUICK-START §11.34 п.9 (добавлено): подсказка под полем нажимается и ставит `typeOptions.hintFill` («Мой: @…»). */
  onFill?: (value: string) => void;
}) {
  const desc = fieldDesc(p, help);
  const fill = (p.typeOptions as { hintFill?: unknown } | undefined)?.hintFill;
  // Без подписи вовсе (`typeOptions.noCap`): экран группы быстрого запуска — имя группы уже стоит заголовком
  // экрана (QUICK-START §11.15); пустой displayName сюда не годится — он падает в автоподпись по имени поля.
  const noCap = (p.typeOptions as { noCap?: boolean } | undefined)?.noCap === true;
  // ПОДПИСЬ — НАД ПУЗЫРЁМ, а не внутри него (владелец 2026-08-10, шаги мастера:
  // «заголовка над пузырём когда нет — смотрится плохо»). Классы отдельные, чтобы
  // оформление жило в kit.css: сам рендерер своих стилей не заводит.
  return (
    <div className={`${styles.pfField} kf-blk`} data-param={p.name}>
      {noCap ? null : (
        <label className={`${styles.pfLabel} kf-blk__cap`}>
          {label(p)}
          {/* Пустой dict.required = приписку не рисуем (мастера: владелец 2026-08-05
              «не пиши везде обязательно» — пустоту подсвечивает честная валидация). */}
          {p.required && dict.required ? <span className={styles.pfReq}> {dict.required}</span> : null}
        </label>
      )}
      <div className="kf-blk__body">{children}</div>
      {/* ПОДСКАЗКА — ПОД ПУЗЫРЁМ СНАРУЖИ (кадры iOS W13/W15, ТЗ WEB-PARITY-3 М12/М21: общий
          рецепт поля мастера «подпись сверху · карточка · подсказка снизу снаружи»). */}
      {desc && onFill && typeof fill === 'string' ? (
        <KBareButton className={`${styles.pfDesc} kf-blk__foot kf-blk__fill`} onClick={() => onFill(fill)}>
          {desc}
        </KBareButton>
      ) : desc ? (
        <div className={`${styles.pfDesc} kf-blk__foot`}>{desc}</div>
      ) : null}
    </div>
  );
}

// Сложное поле (collection/fixedCollection/json/…): локальный текст-стейт, наверх уходит
// только распарсившийся JSON (кривой ввод остаётся в поле с пометкой, значение не портим).
function ComplexField({
  p,
  value,
  dict,
  help,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  onValue: (v: unknown) => void;
}) {
  const initial = useMemo(() => JSON.stringify(value ?? p.default ?? {}, null, 2), [value, p.default]);
  const [text, setText] = useState(initial);
  const [bad, setBad] = useState(false);

  const commit = (raw: string): void => {
    try {
      onValue(JSON.parse(raw));
      setBad(false);
    } catch {
      setBad(true);
    }
  };

  return (
    <FieldShell p={p} dict={dict} help={help}>
      <textarea
        className={`${styles.pfInput} ${styles.pfCode}`}
        rows={Math.min(10, Math.max(3, text.split('\n').length))}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => commit(text)}
        spellCheck={false}
      />
      <div className={`${styles.pfDesc} ${bad ? styles.pfBad : ''}`}>
        {bad ? dict.jsonInvalid : dict.complexHint}
      </div>
    </FieldShell>
  );
}

// ── Рендерер коллекций (STEP-FORM-UX §2): collection/fixedCollection описаны в
// каталоге теми же INodeProperties — редактируем формой с «+ Добавить», а не JSON.
// Незнакомая структура честно падает в ComplexField (JSON) — без обмана.
// Разборщики структур (collectionSubParams / fixedGroups) — в field-dispatch.ts.

// Контекст КОРНЯ узла (значения, слитые с default'ами, + typeVersion) — для «/…»- и
// @version-условий подполей коллекций на любой глубине (Ф13(2)(4)): ставит ParamsForm,
// пропсами вглубь не тащим. Вне провайдера (изолированный рендер поля) — прежнее
// поведение visibleCollectionSubs без корня.
const NodeRootCtx = createContext<{ params: Record<string, unknown>; version?: number } | undefined>(
  undefined,
);

// Карта подписей node_help для ВЛОЖЕННЫХ полей (BUILDER-UX-FIXLIST 2.1): ключ — путь
// поля в каталоге без индексов списков (options.batching.batchSize,
// formFields.values.fieldName). Ставит ParamsForm; Field добирает свою подпись по
// path сам — пропсом help вглубь коллекций ничего тащить не нужно.
const HelpCtx = createContext<Record<string, string> | undefined>(undefined);
/** Мастера шаблонов (владелец 11.08.2026): тумблер с описанием — подпись НАД пузырём,
 *  в строке пузыря описание + переключатель. Вне мастера (формы шага) — прежний вид. */
const ToggleCapsCtx = createContext(false);

// Путь поля → ключ карты подписей: индексы повторяемых групп ([0], [3]) не различаем —
// подпись у всех строк списка одна.
const helpKey = (path: string): string => path.replace(/\[\d+\]/g, '');

// hidden-подполя коллекций не рисуются и не предлагаются в «+ Добавить»; show/hide
// внутри элемента считаются от значений соседей (§2.3 вложенность, visibleCollectionSubs),
// корневые «/…»-условия и @version — от контекста корня узла (NodeRootCtx).
const visibleSubs = (
  subs: CatalogParam[],
  item: Record<string, unknown>,
  root?: { params: Record<string, unknown>; version?: number },
): CatalogParam[] =>
  visibleCollectionSubs(subs, item, root).filter((s) => dispatchField(s).kind !== 'hidden');

// Стартовое значение добавляемого подпараметра — его default или пустышка по типу.
function emptyFor(p: CatalogParam): unknown {
  if (p.default !== undefined) return typeof p.default === 'object' && p.default !== null
    ? JSON.parse(JSON.stringify(p.default))
    : p.default;
  const t = p.type ?? 'string';
  if (t === 'boolean') return false;
  if (t === 'number') return 0;
  if (t === 'collection' || t === 'fixedCollection') return {};
  if (t === 'multiOptions') return [];
  return '';
}

// Пустой объект-строка повторяемой группы: required-поля группы сразу с пустышками.
function emptyGroupItem(g: FcGroup): Record<string, unknown> {
  const item: Record<string, unknown> = {};
  for (const v of g.values) if (v.required || v.default !== undefined) item[v.name] = emptyFor(v);
  return item;
}

function CollectionField({
  p,
  value,
  dict,
  help,
  data,
  path,
  loadDyn,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  data?: DataPick;
  /** §2.4: полный путь этого поля (для живых вложенных полей). */
  path?: string;
  loadDyn?: DynOptionsLoader;
  onValue: (v: unknown) => void;
}) {
  const root = useContext(NodeRootCtx);
  const obj = isPlainObject(value) ? value : {};
  const subs = visibleSubs(collectionSubParams(p), obj, root);
  const added = subs.filter((s) => s.name in obj);
  const addable = subs.filter((s) => !(s.name in obj));
  // Добавленные в этом открытии блоки (например Response у HTTP-запроса) — с анимацией.
  const [initialKeys] = useState<Set<string>>(() => new Set(added.map((s) => s.name)));
  return (
    <FieldShell p={p} dict={dict} help={help}>
      <div className={styles.pfColl}>
        {added.map((s) => (
          <div
            key={s.name}
            className={
              initialKeys.has(s.name)
                ? styles.pfCollItem
                : `${styles.pfCollItem} ${styles.pfRowNew}`
            }
          >
            <div className={styles.pfCollItemField}>
              <Field
                p={s}
                value={obj[s.name]}
                dict={dict}
                data={data}
                path={`${path ?? p.name}.${s.name}`}
                loadDyn={loadDyn}
                onValue={(v) => onValue({ ...obj, [s.name]: v })}
              />
            </div>
            <button
              type="button"
              className={styles.pfCollRemove}
              title={dict.collRemove}
              aria-label={dict.collRemove}
              onClick={() => {
                const next = { ...obj };
                delete next[s.name];
                onValue(Object.keys(next).length > 0 ? next : undefined);
              }}
            >
              ✕
            </button>
          </div>
        ))}
        {addable.length > 0 && (
          <SmartSelect
            className={`${styles.pfInput} ${styles.pfCollAdd}`}
            ariaLabel={dict.collAdd}
            placeholder={dict.collAdd}
            value=""
            options={addable.map((s) => ({ value: s.name, label: label(s) }))}
            onChange={(name) => {
              const s = addable.find((x) => x.name === name);
              if (s) onValue({ ...obj, [s.name]: emptyFor(s) });
            }}
          />
        )}
      </div>
    </FieldShell>
  );
}

// collection + multipleValues (§2.1): значение — массив объектов коллекции; каждый
// элемент показывает ВСЕ видимые подполя (github labels, activeCampaign orderProducts).
function CollectionMultiField({
  p,
  value,
  dict,
  help,
  data,
  path,
  loadDyn,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  data?: DataPick;
  path?: string;
  loadDyn?: DynOptionsLoader;
  onValue: (v: unknown) => void;
}) {
  // Новый элемент сидится по ВСЕМ нескрытым подполям (дефолты условных тоже — их
  // покажет смена условия); видимость строк считается по значениям каждого элемента.
  const root = useContext(NodeRootCtx);
  const subs = collectionSubParams(p).filter((s) => dispatchField(s).kind !== 'hidden');
  const items: Record<string, unknown>[] = Array.isArray(value) ? value.filter(isPlainObject) : [];
  // Строки, добавленные в ЭТОМ открытии формы, появляются с анимацией (pfRowNew);
  // строки, что были при открытии, не дёргаются (владелец 2026-07-17).
  const [initialCount] = useState(() => items.length);
  const commit = (next: Record<string, unknown>[]): void =>
    onValue(next.length > 0 ? next : undefined);
  const newItem = (): Record<string, unknown> =>
    Object.fromEntries(subs.map((s) => [s.name, emptyFor(s)]));
  return (
    <FieldShell p={p} dict={dict} help={help}>
      <div className={styles.pfColl}>
        {items.map((item, i) => (
          <div
            key={i}
            className={
              i >= initialCount ? `${styles.pfFcItem} ${styles.pfRowNew}` : styles.pfFcItem
            }
          >
            <button
              type="button"
              className={styles.pfCollRemove}
              title={dict.collRemove}
              aria-label={dict.collRemove}
              onClick={() => commit(items.filter((_, j) => j !== i))}
            >
              ✕
            </button>
            {visibleSubs(subs, item, root).map((s) => (
              <Field
                key={s.name}
                p={s}
                value={item[s.name]}
                dict={dict}
                data={data}
                path={`${path ?? p.name}[${i}].${s.name}`}
                loadDyn={loadDyn}
                onValue={(v) => {
                  const nextItem = { ...item };
                  if (v === undefined) delete nextItem[s.name];
                  else nextItem[s.name] = v;
                  commit(items.map((x, j) => (j === i ? nextItem : x)));
                }}
              />
            ))}
          </div>
        ))}
        <button type="button" className={styles.pfFcAdd} onClick={() => commit([...items, newItem()])}>
          {dict.collAddItem}
        </button>
      </div>
    </FieldShell>
  );
}

function FixedCollectionField({
  p,
  value,
  dict,
  help,
  data,
  path,
  loadDyn,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  data?: DataPick;
  path?: string;
  loadDyn?: DynOptionsLoader;
  onValue: (v: unknown) => void;
}) {
  const root = useContext(NodeRootCtx);
  const groups = fixedGroups(p);
  const multiple = typeOpt(p, 'multipleValues') === true;
  const obj = isPlainObject(value) ? value : {};
  // Появление новых строк с анимацией — счётчик строк каждой группы на момент открытия.
  const [initialByGroup] = useState<Record<string, number>>(() => {
    const snap: Record<string, number> = {};
    for (const g of groups) {
      const raw = obj[g.name];
      snap[g.name] = multiple ? (Array.isArray(raw) ? raw.length : 0) : isPlainObject(raw) ? 1 : 0;
    }
    return snap;
  });
  const setGroup = (g: string, v: unknown): void => {
    const next = { ...obj, [g]: v };
    if (v === undefined) delete next[g];
    onValue(Object.keys(next).length > 0 ? next : undefined);
  };
  return (
    <FieldShell p={p} dict={dict} help={help}>
      <div className={styles.pfColl}>
        {groups.map((g) => {
          const raw = obj[g.name];
          const items: Record<string, unknown>[] = multiple
            ? Array.isArray(raw)
              ? raw.filter(isPlainObject)
              : []
            : isPlainObject(raw)
              ? [raw]
              : [];
          return (
            <div key={g.name}>
              {groups.length > 1 && <div className={styles.pfFcGroup}>{g.displayName || g.name}</div>}
              {items.map((item, i) => (
                <div
                  key={i}
                  className={
                    i >= (initialByGroup[g.name] ?? 0)
                      ? `${styles.pfFcItem} ${styles.pfRowNew}`
                      : styles.pfFcItem
                  }
                >
                  {multiple && (
                    <button
                      type="button"
                      className={styles.pfCollRemove}
                      title={dict.collRemove}
                      aria-label={dict.collRemove}
                      onClick={() =>
                        setGroup(
                          g.name,
                          items.length > 1 ? items.filter((_, j) => j !== i) : undefined,
                        )
                      }
                    >
                      ✕
                    </button>
                  )}
                  {visibleSubs(g.values, item, root).map((s) => (
                    <Field
                      key={s.name}
                      p={s}
                      value={item[s.name]}
                      dict={dict}
                      data={data}
                      path={`${path ?? p.name}.${g.name}${multiple ? `[${i}]` : ''}.${s.name}`}
                      loadDyn={loadDyn}
                      onValue={(v) => {
                        const nextItem = { ...item };
                        if (v === undefined) delete nextItem[s.name];
                        else nextItem[s.name] = v;
                        setGroup(g.name, multiple ? items.map((x, j) => (j === i ? nextItem : x)) : nextItem);
                      }}
                    />
                  ))}
                </div>
              ))}
              {(multiple || items.length === 0) && (
                <button
                  type="button"
                  className={styles.pfFcAdd}
                  onClick={() =>
                    setGroup(g.name, multiple ? [...items, emptyGroupItem(g)] : emptyGroupItem(g))
                  }
                >
                  {dict.collAddItem}
                  {groups.length > 1 ? ` — ${g.displayName || g.name}` : ''}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </FieldShell>
  );
}

// ── resourceLocator (§2.1): переключатель «как указать» + виджет режима ────────────────
// list — живой поиск с инстанса (liveOpts грузит Field); id/url/name… — текст с подписью
// режима, placeholder и regex-валидацией из формата. Значение — {__rl, mode, value}.
const RL_MODE_WORDS: Record<'ru' | 'en', Record<string, string>> = {
  ru: { list: 'Из списка', id: 'ID', url: 'Ссылка', name: 'Имя', username: 'Логин', email: 'Почта' },
  en: { list: 'From list', id: 'By ID', url: 'By URL', name: 'By name', username: 'By username', email: 'By email' },
};

function ResourceLocatorField({
  p,
  value,
  dict,
  help,
  liveOpts,
  filterRequired,
  onFilter,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  liveOpts?: Array<{ value: string; label: string }>;
  /** §2.2 searchFilterRequired: без текста поиска инстанс список не отдаёт. */
  filterRequired?: boolean;
  /** §2.2 searchable: есть живой поиск — рисуем поле-фильтр, текст уходит с debounce. */
  onFilter?: (filter: string) => void;
  onValue: (v: unknown) => void;
}) {
  const modes = rlModes(p);
  // Поле-фильтр поиска (searchListMethod): локальный текст, наверх — с задержкой,
  // чтобы каждый символ не дёргал инстанс живым запросом.
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!onFilter) return;
    const t = setTimeout(() => onFilter(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search, onFilter]);
  const def = isPlainObject(p.default) ? p.default : {};
  const rl = isPlainObject(value) ? value : {};
  const modeName =
    typeof rl.mode === 'string' ? rl.mode : typeof def.mode === 'string' ? def.mode : modes[0]!.name;
  const mode = modes.find((m) => m.name === modeName) ?? modes[0]!;
  // Строитель мог положить голую строку — не теряем, показываем в текущем режиме.
  const val =
    typeof value === 'string'
      ? value
      : typeof rl.value === 'string' || typeof rl.value === 'number'
        ? String(rl.value)
        : '';
  const cachedName = typeof rl.cachedResultName === 'string' ? rl.cachedResultName : undefined;
  const commit = (m: string, v: string, cached?: string): void =>
    onValue({ __rl: true, mode: m, value: v, ...(cached ? { cachedResultName: cached } : {}) });
  // Валидация формата режима (regex из mode.validation); выражения не проверяем.
  let bad = false;
  if (mode.regex && val && !parseExpression(val)?.full) {
    try {
      bad = !new RegExp(mode.regex).test(val);
    } catch {
      bad = false;
    }
  }
  const listWithOpts = mode.name === 'list' && !!liveOpts && liveOpts.length > 0;
  const known = listWithOpts && liveOpts!.some((o) => o.value === val);
  return (
    <FieldShell p={p} dict={dict} help={help}>
      {modes.length > 1 && (
        <div className={styles.pfRlModes}>
          {modes.map((m) => (
            <button
              key={m.name}
              type="button"
              className={`${styles.pfRlMode}${m.name === mode.name ? ` ${styles.pfRlModeOn}` : ''}`}
              aria-pressed={m.name === mode.name}
              onClick={() => commit(m.name, val)}
            >
              {RL_MODE_WORDS[dict.lang][m.name] ?? m.displayName ?? m.name}
            </button>
          ))}
        </div>
      )}
      {/* Живой поиск по списку (§2.2 searchable/searchFilterRequired): фильтр уходит
          на инстанс, список сужается там — не среди уже загруженного. */}
      {mode.name === 'list' && onFilter && (
        <input
          type="search"
          className={styles.pfInput}
          value={search}
          placeholder={dict.rlSearch}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      {listWithOpts ? (
        <SmartSelect
          className={styles.pfInput}
          ariaLabel={label(p)}
          placeholder="—"
          value={val || ''}
          options={[
            // незнакомое текущее значение не теряем — показываем сохранённым именем
            ...(val && !known ? [{ value: val, label: cachedName ?? val }] : []),
            ...liveOpts!,
          ]}
          onChange={(v) => {
            if (!v) return;
            commit('list', v, liveOpts!.find((o) => o.value === v)?.label);
          }}
        />
      ) : mode.name === 'list' && filterRequired && !search.trim() ? (
        <div className={styles.pfDesc}>{dict.rlSearchType}</div>
      ) : (
        <>
          {/* Аудит 6.2: режим «Из списка» без вариантов показывал голый пустой инпут —
              человек не понимал, что списка нет. Честная строка + ручной ввод остаётся. */}
          {mode.name === 'list' && <div className={styles.pfDesc}>{dict.rlEmpty}</div>}
          <input
            type="text"
            className={styles.pfInput}
            value={val}
            placeholder={mode.placeholder ?? p.placeholder}
            onChange={(e) => commit(mode.name, e.target.value)}
          />
        </>
      )}
      {bad && <div className={`${styles.pfDesc} ${styles.pfBad}`}>{dict.rlInvalid}</div>}
      {!bad && mode.hint && <div className={styles.pfDesc}>{stripHtml(mode.hint)}</div>}
    </FieldShell>
  );
}

// ── resourceMapper (§2.1): «сопоставление колонок» ──────────────────────────────────────
// Схема назначения (колонки таблицы/базы) приезжает живым запросом (resourceMapperMethod →
// /resource-mapper-fields), человек заполняет строки «колонка ← значение» всеми способами
// (руками / из данных / формула). Значение — n8n ResourceMapperValue: { mappingMode, value,
// matchingColumns?, schema? }. Схему сохраняем В ЗНАЧЕНИИ: код узлов (postgres и др.) читает
// её при прогоне, а форма рисует поля и без живого запроса (нет креда — но схема уже была).
interface RmField {
  id: string;
  label: string;
  required?: boolean;
  fieldType?: string;
  canMatch?: boolean;
  defaultMatch?: boolean;
  options?: Array<{ value: string; label: string }>;
}

const rmFieldsFromLive = (liveOpts?: DynOption[]): RmField[] | null =>
  liveOpts && liveOpts.length > 0
    ? liveOpts.map((o) => ({
        id: o.value,
        label: o.label,
        required: o.required,
        fieldType: o.fieldType,
        canMatch: o.canMatch,
        defaultMatch: o.defaultMatch,
        options: o.options,
      }))
    : null;

// Схема из сохранённого значения (n8n-формат ResourceMapperField[]) — фолбэк без живого запроса.
function rmFieldsFromSchema(schema: unknown): RmField[] | null {
  if (!Array.isArray(schema)) return null;
  const out: RmField[] = [];
  for (const s of schema) {
    if (!isPlainObject(s) || typeof s.id !== 'string' || s.display === false) continue;
    out.push({
      id: s.id,
      label: typeof s.displayName === 'string' && s.displayName ? s.displayName : s.id,
      required: s.required === true,
      fieldType: typeof s.type === 'string' ? s.type : undefined,
      canMatch: s.canBeUsedToMatch === true,
      defaultMatch: s.defaultMatch === true,
      options: Array.isArray(s.options)
        ? s.options.filter(isPlainObject).map((o) => ({ value: String(o.value), label: String(o.name ?? o.value) }))
        : undefined,
    });
  }
  return out.length > 0 ? out : null;
}

// Тип колонки n8n → тип поля формы: строка «колонка ← значение» рендерится обычным Field,
// плашка-выражение и пикер «из данных» достаются бесплатно.
const RM_PARAM_TYPE: Record<string, string> = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  dateTime: 'dateTime',
  time: 'string',
  options: 'options',
  object: 'json',
  array: 'json',
  url: 'string',
  email: 'string',
};

function rmRowParam(f: RmField): CatalogParam {
  const mapped = f.fieldType ? RM_PARAM_TYPE[f.fieldType] : undefined;
  const hasOpts = !!f.options && f.options.length > 0;
  const type = mapped === 'options' && !hasOpts ? 'string' : (mapped ?? 'string');
  return {
    name: f.id,
    displayName: f.label,
    type,
    ...(type === 'options' ? { options: f.options!.map((o) => ({ name: o.label, value: o.value })) } : {}),
    ...(f.required ? { required: true } : {}),
  };
}

function ResourceMapperField({
  p,
  value,
  dict,
  help,
  data,
  liveOpts,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  data?: DataPick;
  liveOpts?: DynOption[];
  onValue: (v: unknown) => void;
}) {
  const cfg = rmConfig(p)!;
  const def = isPlainObject(p.default) ? p.default : {};
  const rm = isPlainObject(value) ? value : def;
  const fields = rmFieldsFromLive(liveOpts) ?? rmFieldsFromSchema(rm.schema);
  const auto = rm.mappingMode === 'autoMapInputData';
  const vals = isPlainObject(rm.value) ? rm.value : {};
  const needMatch = cfg.mode === 'update' || cfg.mode === 'upsert';
  const savedMatch = Array.isArray(rm.matchingColumns)
    ? rm.matchingColumns.filter((x): x is string => typeof x === 'string')
    : [];
  const matching =
    needMatch && fields && savedMatch.length === 0
      ? fields.filter((f) => f.defaultMatch).map((f) => f.id)
      : savedMatch;
  // Живая схема пришла → кладём её n8n-формат в значение при каждом изменении.
  const schema =
    fields && liveOpts && liveOpts.length > 0
      ? fields.map((f) => ({
          id: f.id,
          displayName: f.label,
          required: f.required === true,
          defaultMatch: f.defaultMatch === true,
          display: true,
          canBeUsedToMatch: f.canMatch === true,
          ...(f.fieldType ? { type: f.fieldType } : {}),
          ...(f.options && f.options.length > 0
            ? { options: f.options.map((o) => ({ name: o.label, value: o.value })) }
            : {}),
        }))
      : rm.schema;

  const commit = (patch: Record<string, unknown>): void =>
    onValue({
      ...rm,
      mappingMode: auto ? 'autoMapInputData' : 'defineBelow',
      value: Object.keys(vals).length > 0 ? vals : null,
      ...(needMatch ? { matchingColumns: matching } : {}),
      ...(schema !== undefined ? { schema } : {}),
      ...patch,
    });

  // Схемы нет (нет креда, не выбран родитель-источник, запрос упал). Пока человек
  // ничего не вводил (значение пустое/дефолтное) — ТОЛЬКО человеческая подсказка, без
  // сырого JSON: новичок на шаге «Таблица данных» видел страшное
  // {"value":null,"mappingMode":…} обязательным полем (аудит 6.2). JSON-фолбэк остаётся
  // для непустого значения — ремонт сохранённого маппинга без схемы.
  if (!fields) {
    const untouched =
      value === undefined || value === null || JSON.stringify(value) === JSON.stringify(def);
    if (untouched) {
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <div className={styles.pfDesc}>{dict.rmNeedSchema}</div>
        </FieldShell>
      );
    }
    return <ComplexField p={p} value={value} dict={dict} help={dict.rmNeedSchema} onValue={onValue} />;
  }

  const setVal = (id: string, v: unknown): void => {
    const next = { ...vals };
    if (v === undefined) delete next[id];
    else next[id] = v;
    commit({ value: Object.keys(next).length > 0 ? next : null });
  };
  const canMatch = fields.filter((f) => f.canMatch);
  // Показаны: обязательные, колонки-совпадения и уже заполненные; остальные — «+ Добавить».
  const shown = auto ? [] : fields.filter((f) => f.required || matching.includes(f.id) || f.id in vals);
  const addable = auto ? [] : fields.filter((f) => !shown.includes(f));

  return (
    <FieldShell p={p} dict={dict} help={help}>
      {cfg.supportAutoMap && (
        <div className={styles.pfRlModes}>
          {(
            [
              ['defineBelow', dict.rmDefine],
              ['autoMapInputData', dict.rmAuto],
            ] as const
          ).map(([m, word]) => {
            const on = (m === 'autoMapInputData') === auto;
            return (
              <button
                key={m}
                type="button"
                className={`${styles.pfRlMode}${on ? ` ${styles.pfRlModeOn}` : ''}`}
                aria-pressed={on}
                onClick={() => commit({ mappingMode: m })}
              >
                {word}
              </button>
            );
          })}
        </div>
      )}
      {needMatch && canMatch.length > 0 && (
        <div className={styles.pfField}>
          <label className={styles.pfLabel}>{dict.rmMatch}</label>
          {cfg.multiKeyMatch ? (
            <div className={styles.pfChecks}>
              {canMatch.map((f) => {
                const on = matching.includes(f.id);
                return (
                  <label key={f.id} className={styles.pfCheckRow}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) =>
                        commit({
                          matchingColumns: e.target.checked
                            ? [...matching, f.id]
                            : matching.filter((x) => x !== f.id),
                        })
                      }
                    />
                    <span>{f.label}</span>
                  </label>
                );
              })}
            </div>
          ) : (
            <SmartSelect
              className={styles.pfInput}
              ariaLabel={dict.rmMatch}
              placeholder="—"
              value={matching[0] ?? ''}
              options={canMatch.map((f) => ({ value: f.id, label: f.label }))}
              onChange={(id) => {
                if (id) commit({ matchingColumns: [id] });
              }}
            />
          )}
        </div>
      )}
      {auto ? (
        <div className={styles.pfDesc}>{dict.rmAutoHint}</div>
      ) : (
        <div className={styles.pfColl}>
          {shown.map((f) => (
            <div key={f.id} className={styles.pfCollItem}>
              <div className={styles.pfCollItemField}>
                <Field p={rmRowParam(f)} value={vals[f.id]} dict={dict} data={data} onValue={(v) => setVal(f.id, v)} />
              </div>
              {!f.required && !matching.includes(f.id) && (
                <button
                  type="button"
                  className={styles.pfCollRemove}
                  title={dict.collRemove}
                  aria-label={dict.collRemove}
                  onClick={() => setVal(f.id, undefined)}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {addable.length > 0 && (
            <SmartSelect
              className={`${styles.pfInput} ${styles.pfCollAdd}`}
              ariaLabel={dict.rmAdd}
              placeholder={dict.rmAdd}
              value=""
              options={addable.map((f) => ({ value: f.id, label: f.label }))}
              onChange={(id) => {
                if (id) setVal(id, '');
              }}
            />
          )}
        </div>
      )}
    </FieldShell>
  );
}

// Синтетический строковый подпараметр (левые/правые части условий, имя/значение поля):
// рендерим через Field — плашка выражений и единый вид достаются бесплатно.
const synth = (name: string, displayName: string): CatalogParam => ({ name, displayName, type: 'string' });

// ── «С чем сравнить» по-человечески: левая часть — известная дата-подстановка
// (date-picks) или оператор дат → правая часть не голый текст, а виджет нужного
// формата (календарь/время/селект дня недели/месяца). Формат при этом гарантирован —
// руками его не промахнёшься (заказ владельца 2026-07-08).
// 'isoDate' — поле ШАГА с датой (образец значения — ISO-строка): сравнивать можно
// только «раньше/позже» (точное «равно» моментов почти никогда не совпадает).
type CompareKind = DateCompare | 'isoDate';

// Образец значения поля шага из пикера: $('Шаг').item.json.путь → sample дерева структур.
function stepFieldSample(data: DataPick | undefined, step: string | undefined, path: string | undefined): string | null {
  if (!data?.steps || !path) return null;
  const src = step ? data.steps.find((s) => s.name === step) : data.steps[0];
  if (!src) return null;
  const walk = (fields: ShapeField[]): string | null => {
    for (const f of fields) {
      if (f.path === path) return f.sample ?? null;
      const kid = f.children ? walk(f.children) : null;
      if (kid !== null) return kid;
    }
    return null;
  };
  return walk(src.fields);
}

function compareKind(leftValue: unknown, opType: string | undefined, data?: DataPick): CompareKind | null {
  const left = typeof leftValue === 'string' ? leftValue : '';
  const parsed = parseExpression(left);
  const pick = datePickByInner(parsed?.inner);
  if (pick && pick.compare !== 'none') return pick.compare;
  // Поле шага с датой: узнаём по образцу значения (ISO-строка) из структур пикера.
  if (parsed?.path) {
    const sample = stepFieldSample(data, parsed.step, parsed.path);
    if (sample !== null && ISO_DATE_SAMPLE_RE.test(sample)) return 'isoDate';
  }
  if (opType === 'dateTime') return 'date';
  return null;
}

// Осмысленные операторы для известного типа левой части (заказ владельца: «раз знаешь
// тип — настраивай список»). null — тип неизвестен, полный список.
const OPS_BY_KIND: Record<CompareKind, string[] | null> = {
  date: ['string:equals', 'string:notEquals', 'dateTime:after', 'dateTime:before'],
  isoDate: ['dateTime:after', 'dateTime:before', 'string:exists', 'string:notExists'],
  dayMonth: ['string:equals', 'string:notEquals'],
  time: ['string:equals', 'string:notEquals'],
  weekday: ['number:equals', 'number:notEquals'],
  month: ['number:equals', 'number:notEquals'],
  number: ['number:equals', 'number:notEquals', 'number:gt', 'number:lt', 'number:gte', 'number:lte'],
  none: null,
};

// В отфильтрованном списке пометки «(число)/(дата)» лишние — тип и так известен,
// а «= (число)» превращается в обычное «равно».
const FILTERED_LABELS: Record<string, Record<'ru' | 'en', string>> = {
  'number:equals': { ru: 'равно', en: 'is equal to' },
  'number:notEquals': { ru: 'не равно', en: 'is not equal to' },
};
function filteredOpLabel(key: string, label: string, lang: 'ru' | 'en'): string {
  return FILTERED_LABELS[key]?.[lang] ?? label.replace(/\s*\((число|number|дата|date)\)$/i, '');
}

const WEEKDAYS: Record<'ru' | 'en', string[]> = {
  ru: ['понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};
const MONTHS: Record<'ru' | 'en', string[]> = {
  ru: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

function CompareValue({
  compare,
  value,
  dict,
  data,
  onValue,
}: {
  compare: CompareKind | null;
  value: unknown;
  dict: ParamsFormDict;
  data?: DataPick;
  onValue: (v: unknown) => void;
}) {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  // Обычное поле (с базой и плашкой): левая часть не про дату ИЛИ справа уже формула.
  if (!compare || parseExpression(text)?.full) {
    return (
      <Field p={synth('rightValue', dict.condRight)} value={value} dict={dict} data={data} onValue={onValue} />
    );
  }
  const shell = (child: React.ReactNode) => (
    <div className={styles.pfField}>
      <label className={styles.pfLabel}>{dict.condRight}</label>
      {child}
    </div>
  );
  if (compare === 'date' || compare === 'isoDate' || compare === 'time') {
    return shell(
      <input
        type={compare === 'time' ? 'time' : 'date'}
        className={styles.pfInput}
        value={text}
        onChange={(e) => onValue(e.target.value)}
      />,
    );
  }
  if (compare === 'weekday' || compare === 'month') {
    const names = compare === 'weekday' ? WEEKDAYS[dict.lang] : MONTHS[dict.lang];
    return shell(
      <SmartSelect
        className={styles.pfInput}
        ariaLabel={dict.condRight}
        placeholder="—"
        value={text}
        options={names.map((n, i) => ({ value: String(i + 1), label: n }))}
        // Число, не строка: левая часть — число, строгая проверка типов n8n.
        onChange={(v) => onValue(Number(v))}
      />,
    );
  }
  if (compare === 'dayMonth') {
    const bad = text !== '' && !/^\d{2}\.\d{2}$/.test(text);
    return shell(
      <>
        <input
          type="text"
          className={styles.pfInput}
          value={text}
          placeholder="08.07"
          inputMode="numeric"
          onChange={(e) => onValue(e.target.value)}
        />
        {bad && <div className={`${styles.pfDesc} ${styles.pfBad}`}>{dict.dayMonthHint}</div>}
      </>,
    );
  }
  return shell(
    <input
      type="text"
      inputMode="numeric"
      className={styles.pfInput}
      value={text}
      // Целое хранится числом (строгая проверка типов n8n); недописанное — строкой.
      onChange={(e) => onValue(/^-?\d+$/.test(e.target.value) ? Number(e.target.value) : e.target.value)}
    />,
  );
}

// ── Конструктор условий (STEP-FORM-UX §3): type=filter (Если/Фильтр/ветки Развилки).
// Форма значения — n8n FilterValue: { combinator, conditions: [{id, leftValue,
// rightValue, operator: {type, operation, singleValue?}}], options }.
function FilterField({
  p,
  value,
  dict,
  help,
  data,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  data?: DataPick;
  onValue: (v: unknown) => void;
}) {
  const v = isPlainObject(value) ? value : {};
  const conditions = (Array.isArray(v.conditions) ? v.conditions : []).filter(isPlainObject);
  const combinator = v.combinator === 'or' ? 'or' : 'and';
  // Служебный блок options n8n сохраняем как есть; для нового значения — канонный.
  const options = isPlainObject(v.options)
    ? v.options
    : { caseSensitive: true, leftValue: '', typeValidation: 'strict', version: 2 };
  const commit = (conds: Record<string, unknown>[], comb: string = combinator): void =>
    onValue({ ...v, options, combinator: comb, conditions: conds });
  const newCondition = (): Record<string, unknown> => ({
    id: crypto.randomUUID(),
    leftValue: '',
    rightValue: '',
    operator: { type: 'string', operation: 'equals' },
  });
  return (
    <FieldShell p={p} dict={dict} help={help}>
      <div className={styles.pfColl}>
        {conditions.length > 1 && (
          <SmartSelect
            className={styles.pfInput}
            ariaLabel={label(p)}
            placeholder="—"
            value={combinator}
            options={[
              { value: 'and', label: dict.condAll },
              { value: 'or', label: dict.condAny },
            ]}
            onChange={(c) => commit(conditions, c)}
          />
        )}
        {conditions.map((c, i) => {
          const op = isPlainObject(c.operator) ? c.operator : {};
          const key = opKey(op);
          const single = findOp(key)?.singleValue === true;
          const patch = (part: Record<string, unknown>): void =>
            commit(conditions.map((x, j) => (j === i ? { ...x, ...part } : x)));
          const kind = compareKind(c.leftValue, findOp(key)?.type, data);
          // Тип левой части известен → в «Условие» только осмысленные операторы
          // (+ текущий, если он вне списка — сохранённое не прячем).
          const allowed = kind ? OPS_BY_KIND[kind] : null;
          const ops = allowed
            ? FILTER_OPS.filter((o) => allowed.includes(`${o.type}:${o.operation}`) || `${o.type}:${o.operation}` === key)
            : FILTER_OPS;
          return (
            <div key={typeof c.id === 'string' ? c.id : i} className={styles.pfFcItem}>
              <button
                type="button"
                className={styles.pfCollRemove}
                title={dict.collRemove}
                aria-label={dict.collRemove}
                onClick={() => commit(conditions.filter((_, j) => j !== i))}
              >
                ✕
              </button>
              <Field
                p={synth('leftValue', dict.condLeft)}
                value={c.leftValue}
                dict={dict}
                data={data}
                onValue={(x) => patch({ leftValue: x ?? '' })}
              />
              <div className={styles.pfField}>
                <label className={styles.pfLabel}>{dict.condOp}</label>
                <SmartSelect
                  className={styles.pfInput}
                  ariaLabel={dict.condOp}
                  placeholder="—"
                  value={findOp(key) ? key : ''}
                  options={ops.map((o) => {
                    const k = `${o.type}:${o.operation}`;
                    return { value: k, label: allowed ? filteredOpLabel(k, o[dict.lang], dict.lang) : o[dict.lang] };
                  })}
                  onChange={(k) => {
                    const o = findOp(k);
                    if (!o) return;
                    patch({
                      operator: { type: o.type, operation: o.operation, ...(o.singleValue ? { singleValue: true } : {}) },
                      ...(o.singleValue ? { rightValue: '' } : {}),
                    });
                  }}
                />
              </div>
              {!single && (
                <CompareValue
                  compare={kind}
                  value={c.rightValue}
                  dict={dict}
                  data={data}
                  onValue={(x) => patch({ rightValue: x ?? '' })}
                />
              )}
            </div>
          );
        })}
        <button type="button" className={styles.pfFcAdd} onClick={() => commit([...conditions, newCondition()])}>
          {dict.condAdd}
        </button>
      </div>
    </FieldShell>
  );
}

// ── Конструктор «поле = значение» (STEP-FORM-UX §3): assignmentCollection (Данные/Set).
// Форма значения: { assignments: [{ id, name, value, type }] }.
function AssignmentsField({
  p,
  value,
  dict,
  help,
  data,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  data?: DataPick;
  onValue: (v: unknown) => void;
}) {
  const v = isPlainObject(value) ? value : {};
  const items = (Array.isArray(v.assignments) ? v.assignments : []).filter(isPlainObject);
  const commit = (next: Record<string, unknown>[]): void => onValue({ ...v, assignments: next });
  return (
    <FieldShell p={p} dict={dict} help={help}>
      <div className={styles.pfColl}>
        {items.map((a, i) => {
          const patch = (part: Record<string, unknown>): void =>
            commit(items.map((x, j) => (j === i ? { ...x, ...part } : x)));
          return (
            <div key={typeof a.id === 'string' ? a.id : i} className={styles.pfFcItem}>
              <button
                type="button"
                className={styles.pfCollRemove}
                title={dict.collRemove}
                aria-label={dict.collRemove}
                onClick={() => commit(items.filter((_, j) => j !== i))}
              >
                ✕
              </button>
              <Field
                p={synth('name', dict.assignName)}
                value={a.name}
                dict={dict}
                onValue={(x) => patch({ name: x ?? '' })}
              />
              <div className={styles.pfField}>
                <label className={styles.pfLabel}>{dict.assignType}</label>
                <SmartSelect
                  className={styles.pfInput}
                  ariaLabel={dict.assignType}
                  placeholder="—"
                  value={typeof a.type === 'string' ? a.type : 'string'}
                  options={ASSIGN_TYPES.map((t) => ({ value: t.value, label: t[dict.lang] }))}
                  onChange={(t) => patch({ type: t })}
                />
              </div>
              <Field
                p={synth('value', dict.assignValue)}
                value={a.value}
                dict={dict}
                data={data}
                onValue={(x) => patch({ value: x ?? '' })}
              />
            </div>
          );
        })}
        <button
          type="button"
          className={styles.pfFcAdd}
          onClick={() =>
            commit([...items, { id: crypto.randomUUID(), name: '', value: '', type: 'string' }])
          }
        >
          {dict.assignAdd}
        </button>
      </div>
    </FieldShell>
  );
}

// Виджеты строковой семьи: общий вход в Field (плашка-выражение, пикер «из данных»),
// конкретный контрол — по вердикту. select-live до загрузки вариантов — тоже текст.
// number тоже здесь (§3.2): у него пикер с фильтром «только числа», а выражение
// строителя показывается той же плашкой, а не голым кодом в числовом поле.
const STRING_WIDGETS = new Set<WidgetName>(['text', 'secret', 'textarea', 'code', 'datetime', 'color', 'select-live', 'number']);

// Кнопка «взять из данных» у поля (общая для плашки §1 и сырого поля §4).
function PickToggle({ dict, open, onToggle }: { dict: ParamsFormDict; open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={`${styles.pfPickBtn}${open ? ` ${styles.pfPickBtnOn}` : ''}`}
      title={dict.pickBtn}
      aria-label={dict.pickBtn}
      aria-expanded={open}
      onClick={onToggle}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <ellipse cx="12" cy="5.5" rx="7.5" ry="3" />
        <path d="M4.5 5.5v13c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3v-13" />
        <path d="M4.5 12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3" />
      </svg>
    </button>
  );
}

// ── PARAM-FORM-DYN: «живые» поля n8n — контракт и детектор живут в field-dispatch.ts;
// реэкспорт сохраняет публичный API пакета (@scriptera/ui → web).
export type { DynFieldQuery, DynOptionsLoader } from './field-dispatch';

function Field({
  p,
  value,
  dict,
  help: helpProp,
  data,
  dynOpts,
  loadDyn,
  depsKey,
  path,
  onValue,
}: {
  p: CatalogParam;
  value: unknown;
  dict: ParamsFormDict;
  help?: string;
  /** Пикер «выбрать из данных» (§4) — есть только у строковых полей, когда web дал данные. */
  data?: DataPick;
  /** Живые варианты поля (см. ParamsForm.dynamicOptions); desc — подпись мелко (§5: цена). */
  dynOpts?: Array<{ value: string; label: string; desc?: string }>;
  loadDyn?: DynOptionsLoader;
  /** Снимок значений полей-родителей (loadOptionsDependsOn): смена → перезапрос вариантов. */
  depsKey?: string;
  /** §2.4: полный путь поля (вложенные строят по вложенности); нет — верхний уровень (name). */
  path?: string;
  onValue: (v: unknown) => void;
}) {
  // Плашка подстановки (STEP-FORM-UX §1): выражение строителя показывается человеком,
  // «изменить» открывает сырое поле. Хуки — до всех ранних return (правила хуков).
  const [showCode, setShowCode] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  // «Сейчас» для предпросмотра подстановок — фиксируется на открытие формы (чистый рендер).
  const [previewNow] = useState(() => new Date());
  // Автоподсказки «Своей формулы» (formula-suggest): позиция каретки в однострочнике —
  // токен внутри {{ … }} фильтрует кандидатов; null = поле не в фокусе, подсказок нет.
  const [caret, setCaret] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const formulaCands = useMemo(() => formulaCandidates(data?.steps, dict.lang), [data?.steps, dict.lang]);
  // 2.1: подпись поля — проп (верхний уровень) или карта node_help по пути (вложенные).
  const helpMap = useContext(HelpCtx);
  const help = helpProp ?? helpMap?.[helpKey(path ?? p.name)];
  const toggleCaps = useContext(ToggleCapsCtx);
  // Вердикт общей таблицы решений (field-dispatch): дальше только маршрутизация по нему.
  const decision = useMemo(() => dispatchField(p), [p]);

  // PARAM-FORM-DYN: динамическое поле n8n (варианты резолвит ИНСТАНС кодом узла через
  // кред). options/multiOptions — typeOptions.loadOptionsMethod; resourceLocator —
  // searchListMethod режима list. Успех → варианты подмешиваются в обычную отрисовку;
  // ошибка/нет креда → поле остаётся свободным вводом (честный фолбэк).
  const dynQuery = useMemo(() => {
    const q = dynFieldQuery(p);
    // §2.4: вложенным живым полям n8n нужен ПОЛНЫЙ путь (parameters.a.g[0].b).
    return q && path && path !== p.name ? { ...q, path } : q;
  }, [p, path]);
  const [fetched, setFetched] = useState<DynOption[] | null>(null);
  // §2.2 searchable/searchFilterRequired: текст поиска resourceLocator (list) — уходит
  // в живой запрос фильтром, список сужает инстанс. Debounce — в ResourceLocatorField.
  const [dynFilter, setDynFilter] = useState('');
  const rlList = dynQuery?.searchListMethod ? rlModes(p).find((m) => m.name === 'list') : undefined;
  // Смена родителя (depsKey) — старые варианты недействительны: сброс прямо в рендере
  // (loadOptionsDependsOn §2.2: колонки от прошлой таблицы не предлагаем), эффект ниже
  // перезапросит по своему deps-массиву.
  const [lastDepsKey, setLastDepsKey] = useState(depsKey);
  if (lastDepsKey !== depsKey) {
    setLastDepsKey(depsKey);
    setFetched(null);
  }
  useEffect(() => {
    if (!loadDyn || !dynQuery || dynOpts?.length) return;
    // searchFilterRequired: без текста поиска инстанс список не отдаёт — ждём ввода.
    if (rlList?.filterRequired && !dynFilter) return;
    let alive = true;
    void loadDyn({ ...dynQuery, ...(dynFilter ? { filter: dynFilter } : {}) })
      .then((opts: DynOption[] | null) => {
        if (alive && opts && opts.length > 0) setFetched(opts);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- перезагрузка по смене метода/родителей/фильтра, не каждого символа params
  }, [loadDyn, dynQuery?.methodName, dynQuery?.searchListMethod, dynQuery?.resourceMapperMethod, !!dynQuery?.loadOptions, dynOpts?.length, depsKey, dynFilter, rlList?.filterRequired]);
  const liveOpts = dynOpts && dynOpts.length > 0 ? dynOpts : fetched ?? undefined;

  if (decision.kind === 'hidden') return null;
  const w = decision.kind === 'widget' ? decision.widget : null;

  if (w === 'notice') {
    return <div className={styles.pfNotice}>{help?.trim() || stripHtml(p.displayName || p.description || '')}</div>;
  }

  // resourceLocator (§2.1): свой виджет — переключатель режимов + list/текст; у поиска
  // с фильтром (searchable/searchFilterRequired) — поле-фильтр, текст уходит в setDynFilter.
  if (w === 'resource-locator') {
    return (
      <ResourceLocatorField
        p={p}
        value={value}
        dict={dict}
        help={help}
        liveOpts={liveOpts}
        filterRequired={rlList?.filterRequired}
        onFilter={rlList?.searchable || rlList?.filterRequired ? setDynFilter : undefined}
        onValue={onValue}
      />
    );
  }
  // workflowSelector — тот же RL-виджет: «из списка» (сценарии инстанса) или ID руками.
  if (w === 'workflow-select') {
    const pWf = { ...p, modes: [{ name: 'list' }, { name: 'id' }] } as CatalogParam;
    return <ResourceLocatorField p={pWf} value={value} dict={dict} help={help} liveOpts={liveOpts} onValue={onValue} />;
  }
  // resourceMapper (§2.1): сопоставление колонок — схема из живого запроса (liveOpts)
  // или из сохранённого значения; совсем без схемы — JSON-фолбэк внутри виджета.
  if (w === 'resource-mapper') {
    return <ResourceMapperField p={p} value={value} dict={dict} help={help} data={data} liveOpts={liveOpts} onValue={onValue} />;
  }

  // Поле с живым внешним списком (напр. «Модель»): выпадашка вместо текста-с-формулами.
  if (liveOpts && liveOpts.length > 0 && w && w !== 'multi-checks' && w !== 'multi-checks-live') {
    const rawVal = value ?? p.default;
    const current = typeof rawVal === 'string' ? rawVal : '';
    const known = liveOpts.some((o) => o.value === current);
    // §5: подпись выбранного варианта (цена модели/особенность) — мелко под селектом.
    const selectedDesc = (liveOpts as Array<{ value: string; desc?: string }>).find(
      (o) => o.value === current,
    )?.desc;
    // Короткий живой список (аккаунты клиента) — строками с галочкой, как и статичные:
    // выпадашка ради двух-трёх аккаунтов прячет то, из чего выбираешь (владелец
    // 2026-08-10). Длинные живые списки (модели) остаются выпадашкой.
    if (liveOpts.length <= 6 && known) {
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <KChoice
            ariaLabel={label(p)}
            value={current}
            options={(liveOpts as Array<{ value: string; label: string; desc?: string }>).map((o) => ({
              value: o.value,
              label: o.label,
              hint: o.desc,
            }))}
            onChange={(v) => {
              if (v) onValue(v);
            }}
          />
        </FieldShell>
      );
    }
    return (
      <FieldShell p={p} dict={dict} help={help}>
        <SmartSelect
          className={styles.pfInput}
          ariaLabel={label(p)}
          placeholder="—"
          value={current || ''}
          options={[
            // незнакомое текущее значение не теряем — показываем как есть
            ...(current && !known ? [{ value: current, label: current }] : []),
            ...liveOpts,
          ]}
          onChange={(v) => {
            if (v) onValue(v);
          }}
        />
        {selectedDesc && <div className={styles.pfOptDesc}>{selectedDesc}</div>}
      </FieldShell>
    );
  }

  if (w === 'checkbox') {
    const desc = fieldDesc(p, help);
    const checked = value === undefined ? p.default === true : value === true;
    // Мастер (toggleCaps + есть описание): подпись — НАД пузырём, как у всех полей
    // (владелец 11.08.2026: «заголовок у пузыря — каждый раз»); в строке пузыря слева
    // описание серым, справа тумблер. Без описания заголовку нечего выносить —
    // остаётся строка-тумблер с подписью (вариант ниже).
    // МАСТЕР — КАРТОЧКА-ТУМБЛЕР КАК В iOS (кадры W12/W17/W18, ТЗ WEB-PARITY-3 М24/М26; владелец
    // 02.10.2026: веб повторяет снимок): заголовок и подзаголовок в самой карточке, тумблер справа
    // по центру — тумблер набора KSwitch, подписи снаружи нет.
    if (toggleCaps) {
      return (
        <div className={`${styles.pfField} kf-blk`}>
          <div className="kf-blk__body">
            <KSwitch checked={checked} onChange={onValue} label={label(p)} {...(desc ? { hint: desc } : {})} />
          </div>
        </div>
      );
    }
    // kf-blk--solid: блок-пузырь без выносной подписи — у тумблера подпись стоит в
    // самой строке рядом с переключателем.
    return (
      <div className={`${styles.pfField} kf-blk kf-blk--solid`}>
        <label className={styles.pfCheckRow}>
          <input type="checkbox" checked={checked} onChange={(e) => onValue(e.target.checked)} />
          <span className={styles.pfLabel}>{label(p)}</span>
        </label>
        {desc && <div className={styles.pfDesc}>{desc}</div>}
      </div>
    );
  }

  if (w === 'select') {
    const opts = scalarOptions(p);
    if (opts.length > 0) {
      const current = value !== undefined ? value : p.default;
      const idx = opts.findIndex((o) => o.value === current);
      // ВАРИАНТЫ С ПОЯСНЕНИЕМ — СТРОКАМИ С ГАЛОЧКОЙ, А НЕ ВЫПАДАШКОЙ (владелец
      // 2026-08-10, образец — «Tool access» в приложении Claude). Причина: у таких
      // вариантов длинная подпись и своё пояснение; всплывающий список ложился поверх
      // формы, прятал поле, к которому относится, и показывал пояснение только у уже
      // выбранного. Порог 6: дальше список перестаёт помещаться на экран — там
      // по-прежнему выпадашка.
      // Короткий список — ВСЕГДА строками, с пояснениями или без: иначе на одной
      // странице соседствуют два разных селекта (владелец 10.08.2026). Длинные списки
      // (больше шести) остаются выпадашкой — строками они не помещаются на экран.
      // ТЕЛЕФОН В БРАУЗЕРЕ, БОЛЬШЕ ТРЁХ ВАРИАНТОВ БЕЗ ПОЯСНЕНИЙ — СТРОКА «значение ›» И
      // ЛИСТ ВЫБОРА (ТЗ WEB-PARITY-3 М22/М85, кадры iOS W16/W35: «Что отправить» →
      // «Ссылка ›», лист снизу с галочкой). Короткие списки и варианты С пояснениями
      // остаются строками с галочкой («Когда запускать» М11, «Результат» W53). Лист рисует
      // SmartSelect сам (`wantsPickSheet`).
      // Порог 3 — по кадрам iOS: «Результат» (3 варианта) стоит строками (W53), «Что
      // отправить» (5) — строкой с листом (W35).
      const asRows = opts.length <= 6 && !(toggleCaps && wantsPickSheet() && opts.length > 3 && opts.every((o) => !o.description));
      // «Своё» первым и выделено (QUICK-START §11.7 п.2, §11.12): признак поля `typeOptions.accentFirst`.
      const accentFirst = (p.typeOptions as { accentFirst?: boolean } | undefined)?.accentFirst === true;
      if (asRows) {
        return (
          <FieldShell p={p} dict={dict} help={help}>
            <KChoice
              ariaLabel={label(p)}
              value={idx >= 0 ? String(idx) : ''}
              options={opts.map((o, i) => ({
                value: String(i),
                label: o.name,
                hint: o.description ? stripHtml(o.description) : undefined,
                ...(accentFirst && i === 0 ? { accent: true } : {}),
              }))}
              onChange={(v) => {
                const o = opts[Number(v)];
                if (o) onValue(o.value);
              }}
            />
          </FieldShell>
        );
      }
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <SmartSelect
            className={styles.pfInput}
            ariaLabel={label(p)}
            placeholder="—"
            value={idx >= 0 ? String(idx) : ''}
            options={opts.map((o, i) => ({ value: String(i), label: o.name, ...(accentFirst && i === 0 ? { accent: true } : {}) }))}
            onChange={(v) => {
              const o = opts[Number(v)];
              if (o) onValue(o.value);
            }}
          />
          {/* 10.12: описание выбранного варианта под селектом. */}
          {idx >= 0 && opts[idx]?.description && (
            <div className={styles.pfOptDesc}>{stripHtml(opts[idx]!.description!)}</div>
          )}
        </FieldShell>
      );
    }
  }

  // Мультивыбор: статичные варианты каталога или живые с инстанса (loadOptionsMethod).
  // Живой без загрузившихся вариантов (нет креда/ошибка) падает в JSON-фолбэк ниже.
  if (w === 'multi-checks' || (w === 'multi-checks-live' && liveOpts && liveOpts.length > 0)) {
    const opts =
      w === 'multi-checks'
        ? scalarOptions(p)
        : liveOpts!.map((o): CatalogParamOption => ({ name: o.label, value: o.value }));
    const current: unknown[] = Array.isArray(value) ? value : Array.isArray(p.default) ? p.default : [];
    // Короткий список — ТЕМИ ЖЕ строками с галочкой, что и одиночный выбор (владелец
    // 11.08.2026: «должен был быть выбор с такими галочками, как на соседних
    // страницах» — чекбоксы-тумблеры читались сменой дизайна). Отмечаются несколько.
    // Порог 6 — общий с одиночным селектом; длинные списки остаются чекбоксами
    // в прокручиваемой рамке.
    if (opts.length <= 6) {
      const onIdx = opts.map((o) => current.some((v) => v === o.value));
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <KChoiceMany
            ariaLabel={label(p)}
            values={opts.map((_, i) => String(i)).filter((_, i) => onIdx[i])}
            options={opts.map((o, i) => ({
              value: String(i),
              label: o.name,
              hint: o.description ? stripHtml(o.description) : undefined,
            }))}
            onChange={(vals) => onValue(opts.filter((_, i) => vals.includes(String(i))).map((o) => o.value))}
          />
        </FieldShell>
      );
    }
    return (
      <FieldShell p={p} dict={dict} help={help}>
        <div className={styles.pfChecks}>
          {opts.map((o, i) => {
            const on = current.some((v) => v === o.value);
            return (
              <div key={i}>
                <label className={styles.pfCheckRow}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) =>
                      onValue(e.target.checked ? [...current, o.value] : current.filter((v) => v !== o.value))
                    }
                  />
                  <span>{o.name}</span>
                </label>
                {/* 10.12: описание выбранного события по-человечески, под галкой. */}
                {on && o.description && <div className={styles.pfOptDesc}>{stripHtml(o.description)}</div>}
              </div>
            );
          })}
        </div>
      </FieldShell>
    );
  }

  // Строковая семья (текст/секрет/многострочник/код/дата/цвет/число + фолбэк-текст дыр):
  // общий вход — плашка-выражение и пикер «из данных», конкретика — по виджету.
  const stringLike = w ? STRING_WIDGETS.has(w) : decision.kind === 'raw' && decision.fallback === 'text';
  if (stringLike) {
    const current = value !== undefined ? value : p.default;
    const text = typeof current === 'string' || typeof current === 'number' ? String(current) : '';
    // Значение — выражение n8n целиком → человеческая плашка вместо кода (§1).
    const secretField = typeOpt(p, 'password') === true;
    // §3.1: паспорт входа — режим заполнения (только руками / только список → пикера нет)
    // и тип данных (пикер предлагает только подходящее: число — числа, дата — даты).
    const pass = inputPassport(p);
    const noExpr = pass.fill === 'manual' || pass.fill === 'list';
    const pickable = !!data && !secretField && !noExpr;
    // §3.2/§3б: text и json пикер не сужают; остальное (число/дата/булево/url/binary:*)
    // фильтрует и листья шагов, и «стандартные».
    const accept = pass.data === 'text' || pass.data === 'json' ? undefined : pass.data;
    const parsed = showCode ? null : parseExpression(text);
    // Живая проверка синтаксиса формулы (FORMULA-SUGGEST этап 2): красная приписка
    // и под сырым полем при наборе, и под плашкой (шаг переименовали — формула битая).
    // Имена шагов — ВСЕ верхние (stepNames), не только со структурой (steps).
    const lintIssue = secretField
      ? null
      : lintFormula(text, data ? data.stepNames ?? data.steps?.map((s) => s.name) ?? null : null);
    // §3.3: бинарная плашка — значение поля-ключа найдено в описи бинарников шага
    // («видео (ролик.mp4) — из шага „Генерация ролика"»), рядом кнопка пикера.
    const binHit = (() => {
      if (!pass.binaryKey || showCode || !text || parsed?.full) return null;
      for (const s of data?.steps ?? []) {
        const b = s.binary?.find((x) => x.key === text);
        if (b) return { step: s.name, bin: b };
      }
      return null;
    })();
    if (binHit) {
      const what = `${binKindWord(binHit.bin.mimeType, dict)}${binHit.bin.fileName ? ` (${binHit.bin.fileName})` : ''}`;
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <div className={pickable ? styles.pfPickRow : undefined}>
            <div className={styles.pfExpr}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M21 12.5l-8.5 8.5a5.66 5.66 0 0 1-8-8l8.5-8.5a4 4 0 0 1 5.66 5.66L10.4 18.4a2.12 2.12 0 0 1-3-3l7.8-7.8" />
              </svg>
              <div className={styles.pfExprBody}>
                <div className={styles.pfExprTitle}>{dict.substAuto}</div>
                <div className={styles.pfExprWhat}>
                  {what} — {dict.substFromStep.replace('{step}', binHit.step)}
                </div>
              </div>
              <button type="button" className={styles.pfExprEdit} onClick={() => setShowCode(true)}>
                {dict.substEdit}
              </button>
            </div>
            {pickable && <PickToggle dict={dict} open={pickOpen} onToggle={() => setPickOpen((v) => !v)} />}
          </div>
          {pickable && pickOpen && (
            <DataPickPanel
              data={data!}
              dict={dict}
              accept={accept}
              binaryKey
              onPick={(v) => {
                onValue(v === '' ? undefined : v);
                setPickOpen(false);
                setShowCode(false);
              }}
            />
          )}
        </FieldShell>
      );
    }
    if (parsed?.full) {
      // Известная дата-формула пикера — своё слово («сегодняшняя дата»), без «из шага»;
      // если значение можно посчитать прямо сейчас — показываем его в скобках.
      const dateWord = parsed.inner ? humanDateExpr(parsed.inner, dict.lang) : null;
      const live = parsed.inner ? livePickValue(parsed.inner, previewNow) : null;
      // Формула с функциями (этап 3): плашка показывает и «что получится» на образце —
      // считает formula-eval (без исполнения живых данных; не смогли — молчим честно).
      const formulaLive =
        !dateWord && !parsed.path && parsed.inner ? evalFormulaPreview(parsed.inner, data?.steps, previewNow) : null;
      const what = dateWord
        ? `${dateWord}${live ? ` (${live})` : ''}`
        : parsed.path
          ? humanPath(parsed.path, dict.lang)
          : formulaLive
            ? `${dict.substFormula} (${formulaLive})`
            : dict.substFormula;
      const source = parsed.step ? dict.substFromStep.replace('{step}', parsed.step) : dict.substPrev;
      return (
        <FieldShell p={p} dict={dict} help={help}>
          {/* Кнопка базы живёт и у плашки: «взял поле → передумал → взял другое» без «изменить». */}
          <div className={pickable ? styles.pfPickRow : undefined}>
            <div className={styles.pfExpr}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M13 2L4.5 13.5h6L10 22l8.5-11.5h-6L13 2z" />
              </svg>
              <div className={styles.pfExprBody}>
                <div className={styles.pfExprTitle}>{dict.substAuto}</div>
                <div className={styles.pfExprWhat}>{dateWord ? what : `${what} — ${source}`}</div>
              </div>
              <button type="button" className={styles.pfExprEdit} onClick={() => setShowCode(true)}>
                {dict.substEdit}
              </button>
            </div>
            {pickable && <PickToggle dict={dict} open={pickOpen} onToggle={() => setPickOpen((v) => !v)} />}
          </div>
          {/* Формула битая (обычно — шаг переименовали и $('Шаг') осиротел): честно
              говорим под плашкой, а не молчим до падения прогона. */}
          {lintIssue && <div className={`${styles.pfDesc} ${styles.pfBad}`}>{lintText(lintIssue, dict)}</div>}
          {pickable && pickOpen && (
            <DataPickPanel
              data={data!}
              dict={dict}
              accept={accept}
              binaryKey={pass.binaryKey}
              onPick={(expr) => {
                onValue(expr === '' ? undefined : expr);
                setPickOpen(false);
                setShowCode(false);
              }}
              onCustom={() => {
                setPickOpen(false);
                setShowCode(true);
              }}
            />
          )}
        </FieldShell>
      );
    }
    // Число (§2.2 min/max/precision + §3.2): пикер «из данных» с фильтром «только числа»;
    // выражение уже поймано плашкой выше. Clamp — по уходу фокуса (недописанное «5» при
    // min=10 не должно прыгать в 10 на каждый символ).
    if (w === 'number') {
      const min = typeOpt(p, 'minValue');
      const max = typeOpt(p, 'maxValue');
      const precision = typeOpt(p, 'numberPrecision');
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <div className={pickable ? styles.pfPickRow : undefined}>
            <input
              type="number"
              className={styles.pfInput}
              value={typeof current === 'number' ? current : typeof current === 'string' ? current : ''}
              placeholder={p.placeholder}
              min={typeof min === 'number' ? min : undefined}
              max={typeof max === 'number' ? max : undefined}
              step={typeof precision === 'number' ? 10 ** -precision : undefined}
              onChange={(e) => {
                const n = e.target.value;
                onValue(n === '' ? undefined : Number(n));
              }}
              onBlur={(e) => {
                const n = Number(e.target.value);
                if (e.target.value === '' || Number.isNaN(n)) return;
                if (typeof min === 'number' && n < min) onValue(min);
                else if (typeof max === 'number' && n > max) onValue(max);
              }}
            />
            {pickable && <PickToggle dict={dict} open={pickOpen} onToggle={() => setPickOpen((v) => !v)} />}
          </div>
          {pickable && pickOpen && (
            <DataPickPanel
              data={data!}
              dict={dict}
              accept={accept}
              onPick={(expr) => {
                onValue(expr);
                setPickOpen(false);
              }}
            />
          )}
        </FieldShell>
      );
    }
    // PARAM-FORM-DYN этап 2: dateTime — родной выбор даты-времени (мобильная клавиатура
    // с колёсами), формулы/«из данных» остаются: выражение уже поймано плашкой выше,
    // а кнопка базы живёт рядом с календарём.
    if (w === 'datetime') {
      // n8n хранит ISO; datetime-local хочет YYYY-MM-DDTHH:mm (локальный, без зоны)
      const isoToLocal = (s: string): string => {
        const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(s);
        return m ? `${m[1]}T${m[2]}` : '';
      };
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <div className={pickable ? styles.pfPickRow : undefined}>
            <input
              type="datetime-local"
              className={styles.pfInput}
              value={isoToLocal(text)}
              onChange={(e) => onValue(e.target.value === '' ? undefined : e.target.value)}
            />
            {pickable && <PickToggle dict={dict} open={pickOpen} onToggle={() => setPickOpen((v) => !v)} />}
          </div>
          {pickable && pickOpen && (
            <DataPickPanel
              data={data!}
              dict={dict}
              accept={accept}
              onPick={(expr) => {
                onValue(expr);
                setPickOpen(false);
                setShowCode(false);
              }}
              onCustom={() => {
                setPickOpen(false);
                setShowCode(true);
              }}
            />
          )}
        </FieldShell>
      );
    }
    // color — родная палитра + текст рядом (значение видно и правится руками).
    if (w === 'color') {
      const colorOk = /^#[0-9a-fA-F]{6}$/.test(text);
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              value={colorOk ? text : '#e23570'}
              onChange={(e) => onValue(e.target.value)}
              style={{ width: 42, height: 38, border: 'none', background: 'none', padding: 0 }}
              aria-label={label(p)}
            />
            <input
              className={styles.pfInput}
              value={text}
              placeholder="#rrggbb"
              onChange={(e) => onValue(e.target.value === '' ? undefined : e.target.value)}
            />
          </div>
        </FieldShell>
      );
    }
    // Код (jsCode и т.п., typeOptions.editor) — моноширинный многострочник, а не
    // однострочное поле с простынёй (BUILDER-REPAIR-PLAN этап 3, скрин 2026-07-06).
    if (w === 'code') {
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <textarea
            className={`${styles.pfInput} ${styles.pfCode}`}
            rows={Math.min(14, Math.max(6, text.split('\n').length + 1))}
            value={text}
            placeholder={p.placeholder}
            onChange={(e) => onValue(e.target.value)}
            spellCheck={false}
          />
        </FieldShell>
      );
    }
    if (w === 'textarea') {
      return (
        <FieldShell p={p} dict={dict} help={help}>
          <AutoGrowTextarea
            className={styles.pfInput}
            // Число строк анкеты уезжает CSS (`--ta-rows`): на телефоне в браузере карточка
            // держит высоту родного поля iOS (rows 3 → 128, rows 6 → 224; М5/М17).
            rows={Number(typeOpt(p, 'rows') ?? 2)}
            max={Math.min(Number(typeOpt(p, 'rows') ?? 2) + 10, 14)}
            value={text}
            placeholder={p.placeholder}
            onValue={onValue}
            onBlur={() => {
              // Как у однострочного поля: «{{ … }}» без «=» — достраиваем префикс.
              if (!text.startsWith('=') && text.includes('{{') && text.includes('}}')) onValue(`=${text}`);
            }}
          />
          {lintIssue && <div className={`${styles.pfDesc} ${styles.pfBad}`}>{lintText(lintIssue, dict)}</div>}
        </FieldShell>
      );
    }
    // Пикер «выбрать из данных» (§4): у однострочного текстового поля — кнопка-дерево;
    // клик по полю данных кладёт выражение n8n целиком, оно сразу отрисуется плашкой §1.
    const rawParsed = parseExpression(text);
    const preview =
      rawParsed?.full && rawParsed.inner
        ? (livePickValue(rawParsed.inner, previewNow) ??
          stepFieldSample(data, rawParsed.step, rawParsed.path) ??
          evalFormulaPreview(rawParsed.inner, data?.steps, previewNow))
        : null;
    // Автоподсказки при наборе формулы (бэклог «последний хвост»): каретка внутри
    // {{ … }} → по набранному токену предлагаем поля шагов и стандартные подстановки.
    // Каретка после точки у значимого выражения (FORMULA-SUGGEST этап 1) — функции
    // словаря по типу значения; вне точки — прежний поток кандидатов.
    const dot = caret !== null && !secretField ? formulaDotSuggestions(text, caret, data?.steps, formulaCands, dict.lang) : null;
    const tok = dot ? dot.tok : caret !== null && !secretField ? formulaTokenAt(text, caret) : null;
    const suggests = dot ? dot.list : tok ? filterSuggestions(formulaCands, tok.token) : [];
    const pickSuggest = ({ insert, caretOffset }: FormulaSuggestion): void => {
      const r = applySuggestion(text, tok!, insert, caretOffset);
      onValue(r.text);
      setCaret(r.caret);
      // Каретку возвращаем после ре-рендера контролируемого значения.
      requestAnimationFrame(() => inputRef.current?.setSelectionRange(r.caret, r.caret));
    };
    return (
      <FieldShell p={p} dict={dict} help={help} onFill={(v) => onValue(v)}>
        <div className={pickable ? styles.pfPickRow : undefined}>
          <input
            ref={inputRef}
            type={secretField ? 'password' : 'text'}
            className={styles.pfInput}
            value={text}
            placeholder={p.placeholder}
            onChange={(e) => {
              setCaret(e.target.selectionStart);
              onValue(e.target.value);
            }}
            onSelect={(e) => setCaret((e.target as HTMLInputElement).selectionStart)}
            // «изменить» на плашке открывает код; ушёл фокус, а значение — всё ещё
            // формула целиком → сворачиваем обратно в плашку (скрин владельца 2026-07-08).
            onBlur={() => {
              setCaret(null);
              // Руками набранное «{{ … }}» без «=» n8n исполнил бы буквой — достраиваем
              // префикс сами (как редактор n8n; живой проход владельца 2026-07-17).
              if (!text.startsWith('=') && text.includes('{{') && text.includes('}}')) {
                onValue(`=${text}`);
                return;
              }
              if (parseExpression(text)?.full) setShowCode(false);
            }}
          />
          {pickable && <PickToggle dict={dict} open={pickOpen} onToggle={() => setPickOpen((v) => !v)} />}
        </div>
        {suggests.length > 0 && (
          <div className={styles.pfSuggest}>
            {suggests.map((s, i) => (
              <button
                key={`${s.insert}-${i}`}
                type="button"
                className={styles.pfSuggestItem}
                // preventDefault на mousedown: клик не должен увести фокус из поля
                // (blur спрятал бы список раньше, чем сработает onClick).
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickSuggest(s)}
              >
                <span>{s.label}</span>
                {s.hint && <span className={styles.pfSuggestHint}>{s.hint}</span>}
              </button>
            ))}
          </div>
        )}
        {/* Живая проверка синтаксиса (этап 2). «Пустую» формулу не ругаем, пока человек
            в поле: «Своя формула» вставляет пустые {{ }} — дать спокойно набрать. */}
        {lintIssue && (lintIssue.code !== 'empty' || caret === null) && (
          <div className={`${styles.pfDesc} ${styles.pfBad}`}>{lintText(lintIssue, dict)}</div>
        )}
        {/* Режим формулы: живой предпросмотр «что получится» (известные подстановки —
            считаем сейчас, поля шагов — образец из структур). */}
        {showCode && preview && (
          <div className={styles.pfDesc}>
            {dict.previewNow} {preview}
          </div>
        )}
        {pickable && pickOpen && (
          <DataPickPanel
            data={data!}
            dict={dict}
            accept={accept}
            binaryKey={pass.binaryKey}
            onPick={(expr) => {
              onValue(expr === '' ? undefined : expr);
              setPickOpen(false);
              setShowCode(false);
            }}
            onCustom={() => {
              setPickOpen(false);
              setShowCode(true);
              if (!text.trim()) onValue('={{  }}');
            }}
          />
        )}
      </FieldShell>
    );
  }

  // json — моноширинный многострочник (как editor-поля): 422 поля каталога, раньше
  // рисовались однострочным текстом (аудит PARAM-FORM-DYN 2026-07-11).
  if (w === 'json') {
    const current = value !== undefined ? value : p.default;
    const text =
      typeof current === 'string' ? current : current == null ? '' : JSON.stringify(current, null, 2);
    return (
      <FieldShell p={p} dict={dict} help={help}>
        <textarea
          className={`${styles.pfInput} ${styles.pfCode}`}
          rows={Math.min(12, Math.max(4, text.split('\n').length + 1))}
          value={text}
          placeholder={p.placeholder ?? '{ }'}
          onChange={(e) => onValue(e.target.value === '' ? undefined : e.target.value)}
          spellCheck={false}
        />
      </FieldShell>
    );
  }

  // Конструкторы условий и «поле = значение» (§3).
  if (w === 'filter') {
    return <FilterField p={p} value={value} dict={dict} help={help} data={data} onValue={onValue} />;
  }
  if (w === 'assignments') {
    return <AssignmentsField p={p} value={value} dict={dict} help={help} data={data} onValue={onValue} />;
  }

  // Коллекции — формой (§2); путь и загрузчик живых списков уходят вглубь (§2.4).
  if (w === 'collection') {
    return <CollectionField p={p} value={value} dict={dict} help={help} data={data} path={path} loadDyn={loadDyn} onValue={onValue} />;
  }
  if (w === 'collection-multi') {
    return <CollectionMultiField p={p} value={value} dict={dict} help={help} data={data} path={path} loadDyn={loadDyn} onValue={onValue} />;
  }
  if (w === 'fixed-collection') {
    return <FixedCollectionField p={p} value={value} dict={dict} help={help} data={data} path={path} loadDyn={loadDyn} onValue={onValue} />;
  }

  // Дыры формата с fallback:'json' (см. field-dispatch) и resourceLocator без
  // загрузившегося списка — честный JSON-текст.
  return <ComplexField p={p} value={value} dict={dict} help={help} onValue={onValue} />;
}

// Сложное — в свёрнутое «Продвинутое» (D5, docs/archive/BUILDER-UX-PLAN.md): JSON-типы
// (collection/fixedCollection/json/…) и поля с выражением в дефолте. Наружу остаются
// простые типы, обязательные поля и notice-подсказки.
function isAdvanced(p: CatalogParam): boolean {
  const type = p.type ?? 'string';
  if (p.required || type === 'notice') return false;
  // Сердце конструкторных нод (§3): условия Если/Фильтра, поля «Данных» и сопоставление
  // колонок — главный интерфейс шага, в «Продвинутое» не прячем (у них своя форма, не JSON).
  if (type === 'filter' || type === 'assignmentCollection' || type === 'resourceMapper') return false;
  if (!SIMPLE_TYPES.has(type)) return true;
  return typeof p.default === 'string' && p.default.trimStart().startsWith('={{');
}

// Заполнено ли значение по-настоящему: пустая строка/объект/массив — не в счёт
// (строитель иногда кладёт пустые заготовки — из-за них секцию раскрывать не надо).
function hasValue(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return true;
}

/**
 * Форма параметров шага. Контролируемая: values = step.params, каждое изменение
 * уходит наверх новым объектом (иммутабельность); сохранение решает родитель.
 */
export function ParamsForm({
  params,
  values,
  dict,
  help,
  advancedNames,
  mainNames,
  fieldWarnings,
  dataPick,
  dynamicOptions,
  loadDynOptions,
  nodeVersion,
  keepOrder,
  toggleCaps,
  onChange,
}: {
  params: CatalogParam[];
  values: Record<string, unknown>;
  dict: ParamsFormDict;
  /** typeVersion узла — для @version-условий displayOptions (§2.3). */
  nodeVersion?: number;
  /** Тексты-объяснения (этап 9): по полю (params) и приписка под «Продвинутым» (advanced). */
  help?: { params?: Record<string, string>; advanced?: string | null };
  /** Имена полей, которые принудительно уезжают в «Продвинутое» (техника, этап 10.3). */
  advancedNames?: string[];
  /** Имена полей, которые принудительно ОСТАЮТСЯ в основной секции (§5: медиа-входы
   *  паспорта модели — главный интерфейс шага, хоть и коллекции). Сильнее isAdvanced. */
  mainNames?: string[];
  /** §5: валидация до запуска — человеческая подпись у поля (ключ — имя параметра). */
  fieldWarnings?: Record<string, string>;
  /** Пикер «выбрать из данных» (§4): шаги выше по потоку со структурой; нет — кнопки нет. */
  dataPick?: DataPick;
  /** Живые варианты для полей с внешним списком (напр. «Модель» шага-модели: имена
   *  моделей агрегатора Scriptera). Ключ — имя параметра; есть список → SmartSelect;
   *  desc — подпись варианта мелко (цена в ₽, особенность модели). */
  dynamicOptions?: Record<string, Array<{ value: string; label: string; desc?: string }>>;
  /** PARAM-FORM-DYN: загрузчик «живых» вариантов у динамических полей n8n
   *  (loadOptionsMethod / searchListMethod). null → поле остаётся свободным вводом. */
  loadDynOptions?: DynOptionsLoader;
  /** Рисовать поля СТРОГО в порядке params, без ярусной сортировки Ф19. Для рукописных
   *  анкет (мастера шаблонов): их порядок — решение владельца, а не каша каталога n8n. */
  keepOrder?: boolean;
  /** Мастера шаблонов (владелец 11.08.2026): тумблеру с описанием — подпись НАД пузырём,
   *  в строке описание + переключатель. Формы шага живут без этого флага (прежний вид). */
  toggleCaps?: boolean;
  onChange: (values: Record<string, unknown>) => void;
}) {
  // Контекст корня узла для подполей коллекций (Ф13(2)(4)): значения со слитыми
  // default'ами + версия — «/…»- и @version-условия вглубь без проброса пропсов.
  const rootCtx = useMemo(
    () => ({ params: effectiveNodeParams(params, values, nodeVersion), version: nodeVersion }),
    [params, values, nodeVersion],
  );
  // Канон «предупреждения не вперёд» + «сырые параметры n8n — не интерфейс» (этап 10.5):
  // плашки-параметры n8n (notice/callout — предупреждения, техно-инструкции редактора) в
  // клиентскую форму НЕ выводим — их роль берёт на себя человеческий intro (node_help).
  // Поля, которые ничего не рисуют (dispatchField hidden: type=credentials у Notion и
  // т.п.), выкидываем ЗДЕСЬ же — иначе «Продвинутое» рисует пустую раскрывашку (Ф13(5)).
  const invisible = (p: CatalogParam): boolean => {
    const dec = dispatchField(p);
    return dec.kind === 'hidden' || (dec.kind === 'widget' && dec.widget === 'notice');
  };
  const shown = visibleParams(params, values, nodeVersion).filter((p) => !invisible(p));
  const advSet = new Set(advancedNames ?? []);
  const mainSet = new Set(mainNames ?? []);
  // Ф19: «режимные» поля — на их значения ссылаются displayOptions других полей каталога
  // (resource/operation и подобные). Данные каталога, не словарь имён: такие селекторы
  // всегда наверху и никогда не сворачиваются — они перестраивают форму.
  const modeNames = useMemo(() => {
    const out = new Set<string>();
    for (const p of params) {
      for (const key of [
        ...Object.keys(p.displayOptions?.show ?? {}),
        ...Object.keys(p.displayOptions?.hide ?? {}),
      ]) {
        if (key.startsWith('@')) continue;
        out.add((key.startsWith('/') ? key.slice(1) : key).split('.')[0]!);
      }
    }
    return out;
  }, [params]);
  // «Продвинутое» открыто сразу, если внутри есть заполненное (владелец 2026-07-08):
  // человек должен видеть свои настройки, а не искать их. Считаем ОДИН раз на маунте
  // (useState-инициализатор, ДО раннего return — правила хуков) — дальше секцией
  // управляет пользователь, повторные рендеры её не дёргают.
  const [advOpen] = useState(() =>
    params.some((p) => (advSet.has(p.name) || isAdvanced(p)) && hasValue(values[p.name])),
  );
  // Ф19: состав свёртки «Ещё N настроек» — незаполненные необязательные основной секции.
  // СНИМОК на маунте (как advOpen): живой пересчёт перекладывал бы поле между
  // контейнерами прямо при вводе — размонтирование виджета, потеря фокуса (урок Ф14).
  const [foldedNames] = useState<Set<string>>(() => {
    const out = new Set<string>();
    for (const p of params) {
      if (p.required || modeNames.has(p.name) || (p.type ?? 'string') === 'notice') continue;
      if (mainSet.has(p.name) || advSet.has(p.name) || isAdvanced(p)) continue;
      if (hasValue(values[p.name]) || fieldWarnings?.[p.name]) continue;
      out.add(p.name);
    }
    return out;
  });
  // У шага мастера без полей служебной строки «нет настроек» клиент не видит (К3.6, запрет №7
  // CLAUDE.md): в анкете это не его работа. В конструкторе строка остаётся.
  if (shown.length === 0) return toggleCaps ? null : <div className={styles.pfNone}>{dict.noParams}</div>;
  const hasText = shown.some((p) => (p.type ?? 'string') === 'string');
  // Снимок значений родителей живого поля (loadOptionsDependsOn): имена бывают вида
  // «sheetName.value» — родитель это верхнее поле, берём его значение целиком.
  const depsKeyFor = (p: CatalogParam): string | undefined => {
    const deps = dynFieldQuery(p)?.dependsOn;
    return deps?.length ? JSON.stringify(deps.map((n) => values[n.split('.')[0]!])) : undefined;
  };
  // Обёртка всегда в DOM (класс меняется, структура нет): предупреждение гаснет прямо
  // при вводе, и смена Fragment↔div размонтировала бы виджет — терялся фокус в поле.
  const field = (p: CatalogParam) => (
    <div
      key={p.name}
      className={`${styles.pfFieldWrap}${fieldWarnings?.[p.name] ? ` ${styles.pfFieldBad}` : ''}`}
    >
      <Field
        p={p}
        value={values[p.name]}
        dict={dict}
        data={dataPick}
        dynOpts={dynamicOptions?.[p.name]}
        loadDyn={loadDynOptions}
        depsKey={depsKeyFor(p)}
        help={help?.params?.[p.name]}
        onValue={(v) => {
          const next = { ...values };
          if (v === undefined) delete next[p.name];
          else next[p.name] = v;
          onChange(next);
        }}
      />
      {/* §5 + Ф14: предупреждение (паспорт модели / «обязательное не заполнено») — под полем. */}
      {fieldWarnings?.[p.name] && (
        <div className={`${styles.pfDesc} ${styles.pfBad}`}>{fieldWarnings[p.name]}</div>
      )}
    </div>
  );
  const inAdvanced = (p: CatalogParam): boolean =>
    !mainSet.has(p.name) && (advSet.has(p.name) || isAdvanced(p));
  const main = shown.filter((p) => !inAdvanced(p));
  const adv = shown.filter(inAdvanced);
  // Ф14: красное поле не должно прятаться в свёрнутом «Продвинутом» — раскрываем.
  const advHasBad = adv.some((p) => fieldWarnings?.[p.name]);
  // Ф19 (тема 5в), борьба с кашей полей: основная секция — ярусами по РОЛИ поля.
  // Ярус 0 «режим» — modeNames (перестраивают форму); ярус 1 «вход» — поля, куда кладут
  // данные (строки/json/локаторы/конструкторы); ярус 2 «настройка» — переключатели/
  // числа/даты. Сортировка стабильная: внутри яруса порядок каталога сохранён.
  const tierOf = (p: CatalogParam): number => {
    if (modeNames.has(p.name)) return 0;
    const type = p.type ?? 'string';
    if (['options', 'multiOptions', 'boolean', 'number', 'color', 'dateTime'].includes(type)) return 2;
    return 1;
  };
  const mainOrdered = keepOrder ? main : [...main].sort((a, b) => tierOf(a) - tierOf(b));
  // Свёртка «Ещё N настроек»: включается только когда незаполненной мелочи МНОГО (>6) —
  // пара необязательных полей каши не делает, свёртка на них только мешала бы.
  const foldable = mainOrdered.filter((p) => foldedNames.has(p.name)).length > 6;
  const mainShown = foldable ? mainOrdered.filter((p) => !foldedNames.has(p.name)) : mainOrdered;
  const folded = foldable ? mainOrdered.filter((p) => foldedNames.has(p.name)) : [];
  const foldHasBad = folded.some((p) => fieldWarnings?.[p.name]);
  return (
    <NodeRootCtx.Provider value={rootCtx}>
      <HelpCtx.Provider value={help?.params}>
      <ToggleCapsCtx.Provider value={!!toggleCaps}>
      {/* `fk-pf` — глобальное имя обёртки рендерера для правил набора (kit.css): в сборке
          имя модуля хэшируется и селектор по хвосту `__pf` не ловил — пузырь доставался всей
          пачке полей, подписи оказывались внутри (кадр staging W11/W24, 03.10.2026). */}
      <div className={`${styles.pf} fk-pf`}>
        {mainShown.map(field)}
        {folded.length > 0 && (
          <details className={styles.pfAdv} open={foldHasBad || undefined}>
            <summary className={styles.pfAdvHead}>{dict.moreSettings(folded.length)}</summary>
            <div className={styles.pfAdvBody}>{folded.map(field)}</div>
          </details>
        )}
        {adv.length > 0 && (
          <details className={styles.pfAdv} open={advOpen || advHasBad || undefined}>
            <summary className={styles.pfAdvHead}>{dict.advanced}</summary>
            <div className={styles.pfAdvBody}>
              {help?.advanced && <div className={styles.pfDesc}>{help.advanced}</div>}
              {adv.map(field)}
            </div>
          </details>
        )}
        {/* Пустой exprHint = подсказка выключена (мастера шаблонов: владелец 2026-08-05
            «удали, сложно очень»). */}
        {hasText && dict.exprHint ? <div className={styles.pfDesc}>{dict.exprHint}</div> : null}
      </div>
      </ToggleCapsCtx.Provider>
      </HelpCtx.Provider>
    </NodeRootCtx.Provider>
  );
}
